import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Ship, Facebook, Linkedin, Instagram, Twitter } from "lucide-react";

const SOCIALS = [
    {
        label: "Facebook",
        href: "https://www.facebook.com/share/1PnRj2FZsi/",
        icon: Facebook,
    },
    {
        label: "LinkedIn",
        href: "https://www.linkedin.com/company/carbex-international-private-limited/",
        icon: Linkedin,
    },
    {
        label: "X",
        href: "https://x.com/carbexintpvtltd/status/2054885367514550749?s=20",
        icon: Twitter,
    },
    {
        label: "Instagram",
        href: "https://www.instagram.com/carbexinternational?utm_source=qr&igsh=MWYwOHF4am95dHVnMg==",
        icon: Instagram,
    },
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-coal-800 border-t border-white/10 relative overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    {/* Brand */}
                    <div className="md:col-span-5">
                        <div className="font-geometric leading-none mb-6">
                            <div className="text-white font-extrabold text-2xl tracking-[0.16em]">
                                CARBEX
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="block h-px w-4 bg-ember" />
                                <span className="text-[11px] text-ember font-bold tracking-[0.32em] uppercase">
                                    International
                                </span>
                                <span className="block h-px w-4 bg-ember" />
                            </div>
                            <div className="text-[10px] text-zinc-500 font-semibold tracking-[0.32em] uppercase mt-2">
                                Private Limited
                            </div>
                            <div className="text-[10px] text-amber-400/70 font-mono tracking-[0.18em] uppercase mt-3">
                                Trading Energy · Delivering Certainty
                            </div>
                        </div>
                        <p className="text-zinc-400 leading-relaxed max-w-md mb-6">
                            A leading importer and supplier of premium coal in
                            India — sourcing from Indonesia, South Africa,
                            Australia, USA & Russia. Delivered to industry,
                            consistently.
                        </p>
                        <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase">
                            <Ship className="w-4 h-4 text-ember" />
                            <span>Verified import-export trader</span>
                        </div>

                        {/* Socials */}
                        <div className="mt-8">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold mb-3">
                                Follow
                            </div>
                            <div className="flex items-center gap-3">
                                {SOCIALS.map(({ label, href, icon: Icon }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        data-testid={`social-${label.toLowerCase()}`}
                                        className="w-10 h-10 border border-white/15 hover:border-ember hover:bg-ember/10 text-zinc-300 hover:text-ember flex items-center justify-center transition-colors"
                                    >
                                        <Icon className="w-4 h-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Navigate */}
                    <div className="md:col-span-3">
                        <div className="text-xs tracking-[0.2em] uppercase text-ember font-bold mb-5">
                            Navigate
                        </div>
                        <ul className="space-y-3 text-zinc-400">
                            <li>
                                <Link
                                    to="/about"
                                    className="hover:text-white link-underline"
                                >
                                    About Us
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/products"
                                    className="hover:text-white link-underline"
                                >
                                    Products
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/industries"
                                    className="hover:text-white link-underline"
                                >
                                    Industries Served
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/quality-logistics"
                                    className="hover:text-white link-underline"
                                >
                                    Quality & Logistics
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className="hover:text-white link-underline"
                                >
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="md:col-span-4">
                        <div className="text-xs tracking-[0.2em] uppercase text-ember font-bold mb-5">
                            Get in touch
                        </div>
                        <ul className="space-y-4 text-zinc-300">
                            <li className="flex items-start gap-3">
                                <Phone className="w-4 h-4 text-ember mt-1 shrink-0" />
                                <div className="font-mono text-sm">
                                    <a
                                        href="tel:9872227812"
                                        className="block hover:text-white"
                                    >
                                        +91 98722 27812
                                    </a>
                                    <a
                                        href="tel:8708386260"
                                        className="block hover:text-white"
                                    >
                                        +91 87083 86260
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 text-ember mt-1 shrink-0" />
                                <a
                                    href="mailto:carbexinternational@gmail.com"
                                    className="hover:text-white break-all"
                                >
                                    carbexinternational@gmail.com
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-ember mt-1 shrink-0" />
                                <span className="text-sm text-zinc-400 leading-relaxed">
                                    #6-C, Professor Colony,
                                    <br />
                                    Yamunanagar, Haryana — 135001
                                    <br />
                                    India
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-xs text-zinc-500 tracking-wider uppercase">
                        © {new Date().getFullYear()} Carbex International Pvt
                        Ltd. All rights reserved.
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono tracking-widest uppercase">
                        IND · GLOBAL TRADING DESK · 24×7
                    </div>
                </div>
            </div>
        </footer>
    );
}
